// src/components/admin/MessagesPage.js
import React, { useState, useEffect } from 'react';
import CreatableSelect from 'react-select/creatable'; // Using Creatable just in case, or standard Select
import Select from 'react-select';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { sendMessage, syncMessages, getMessages, getMessagingDevices } from '../../services/api/messageApi';
import './MessagesPage.css';

const MessagesPage = () => {
    // State
    const [devices, setDevices] = useState([]);
    const [deviceOptions, setDeviceOptions] = useState([]);
    const [selectedDevice, setSelectedDevice] = useState(null);
    const [messageText, setMessageText] = useState('');
    const [messagesHistory, setMessagesHistory] = useState([]);

    // Loading States
    const [loadingDevices, setLoadingDevices] = useState(true);
    const [sending, setSending] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    // Initial Load: Fetch Devices
    useEffect(() => {
        loadDevices();
    }, []);

    const loadDevices = async () => {
        setLoadingDevices(true);
        try {
            const list = await getMessagingDevices();
            setDevices(list);

            // Map to react-select options
            const options = list.map(d => {
                const isTwilio = String(d.model) === '1';
                const canSend = isTwilio ? !!d.phone : !!(d.iccid || d.uniqueId);
                const gateway = isTwilio ? 'Twilio' : '1NCE';
                const suffix = canSend ? gateway : `${gateway} — missing ${isTwilio ? 'phone' : 'ICCID'}`;
                return {
                    value: d.id,
                    label: `${d.name} [${suffix}]`,
                    isDisabled: !canSend,
                    device: d
                };
            });
            setDeviceOptions(options);
        } catch (error) {
            console.error('Failed to load devices:', error);
            toast.error('Failed to load device list');
        } finally {
            setLoadingDevices(false);
        }
    };

    // Handler: Device Selection -> Sync & Load History
    const handleDeviceSelect = async (option) => {
        setSelectedDevice(option);
        setMessagesHistory([]); // Clear view temporarily

        if (option) {
            const device = option.device;
            const iccid = device.iccid || device.uniqueId; // Fallback if iccid missing in mapped object

            setRefreshing(true);
            try {
                // Trigger Sync (External -> DB)
                // Note: uniqueId in 1NCE context usually means ICCID, but check your data mapping
                const history = await syncMessages(device);
                setMessagesHistory(history);
            } catch (error) {
                console.error('Sync error:', error);
                toast.warning('Could not sync via gateway, showing local history only');

                // Fallback to local get
                try {
                    const localHistory = await getMessages({ device });
                    setMessagesHistory(localHistory);
                } catch (localError) {
                    toast.error('Failed to load local history');
                }
            } finally {
                setRefreshing(false);
            }
        }
    };

    // Handler: Send Message
    const handleSend = async () => {
        if (!selectedDevice || !messageText.trim()) return;

        const device = selectedDevice.device;

        setSending(true);
        try {
            await sendMessage(device, messageText);
            toast.success('Message sent successfully');
            setMessageText(''); // Clear input

            // Auto-refresh history after short delay to see the "QUEUED/SENT" status
            setTimeout(() => {
                handleRefresh();
            }, 1000);

        } catch (error) {
            console.error('Send failed:', error);
            toast.error(error.message || 'Failed to send message');
        } finally {
            setSending(false);
        }
    };

    // Handler: Refresh Button
    const handleRefresh = async () => {
        if (selectedDevice) {
            await handleDeviceSelect(selectedDevice);
        }
    };

    // Select Styles
    const customSelectStyles = {
        control: (provided, state) => ({
            ...provided,
            minHeight: '42px',
            borderRadius: '8px',
            backgroundColor: 'var(--bg-tertiary)',
            borderColor: state.isFocused ? 'var(--primary)' : 'var(--border-color)',
            boxShadow: state.isFocused ? '0 0 0 2px var(--active-bg)' : 'none',
            cursor: 'pointer',
            transition: 'all 0.2s'
        }),
        menu: (provided) => ({
            ...provided,
            backgroundColor: 'var(--sidebar-bg)',
            border: '1px solid var(--border-color)',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            zIndex: 50
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected
                ? 'var(--primary)'
                : state.isFocused
                    ? 'var(--hover-bg)'
                    : 'transparent',
            color: state.isSelected ? 'white' : 'var(--text-primary)',
            cursor: 'pointer',
            ':active': {
                backgroundColor: 'var(--active-bg)'
            }
        }),
        singleValue: (provided) => ({
            ...provided,
            color: 'var(--text-primary)',
        }),
        input: (provided) => ({
            ...provided,
            color: 'var(--text-primary)',
        }),
        placeholder: (provided) => ({
            ...provided,
            color: 'var(--text-tertiary)',
        }),
        dropdownIndicator: (provided) => ({
            ...provided,
            color: 'var(--text-tertiary)',
            ':hover': {
                color: 'var(--text-secondary)'
            }
        }),
        indicatorSeparator: (provided) => ({
            ...provided,
            backgroundColor: 'var(--border-color)'
        })
    };

    return (
        <div className="messages-page-wrapper">
            <ToastContainer position="top-right" autoClose={3000} />

            <div className="messages-page-container">
                {/* --- Left Column: Send Panel --- */}
                <div className="message-send-panel">
                    <div className="panel-title">
                        <span className="material-icons-outlined">send</span>
                        Send Command
                    </div>

                    <div className="form-group">
                        <label className="form-label">Select Device</label>
                        <Select
                            options={deviceOptions}
                            value={selectedDevice}
                            onChange={handleDeviceSelect}
                            isLoading={loadingDevices}
                            placeholder="Search device..."
                            styles={customSelectStyles}
                            noOptionsMessage={() => "No devices found"}
                        />
                        {selectedDevice && (
                            <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                                IMEI/ICCID: {selectedDevice.device.uniqueId}
                            </div>
                        )}
                    </div>

                    <div className="form-group">
                        <label className="form-label">Message Payload</label>
                        <textarea
                            className="message-textarea"
                            placeholder="Type AT command or message..."
                            value={messageText}
                            onChange={(e) => setMessageText(e.target.value)}
                            disabled={!selectedDevice || sending}
                        />
                    </div>

                    <button
                        className="send-button"
                        onClick={handleSend}
                        disabled={!selectedDevice || !messageText.trim() || sending}
                    >
                        {sending ? (
                            <>Sending...</>
                        ) : (
                            <>
                                <span className="material-icons-outlined">near_me</span>
                                Send Message
                            </>
                        )}
                    </button>

                    <div className="helper-text" style={{ marginTop: 'auto', fontSize: '0.8rem', color: '#94a3b8', textAlign: 'center' }}>
                        Powered by {(String(selectedDevice?.device?.model) === '1' || String(selectedDevice?.device?.attributes?.model) === '1') ? 'Twilio SMS Gateway' : '1NCE SMS Gateway'}
                    </div>
                </div>

                {/* --- Right Column: Inbox --- */}
                <div className="message-history-panel">
                    <div className="history-header">
                        <div className="panel-title" style={{ margin: 0 }}>
                            <span className="material-icons-outlined">history</span>
                            Message History
                        </div>
                        <button
                            className="refresh-btn"
                            onClick={handleRefresh}
                            disabled={refreshing || !selectedDevice}
                        >
                            <span className={`material-icons-outlined ${refreshing ? 'spin' : ''}`}>
                                refresh
                            </span>
                            {refreshing ? 'Syncing...' : 'Refresh / Sync'}
                        </button>
                    </div>

                    <div className="history-content">
                        {!selectedDevice ? (
                            <div className="empty-state">
                                <span className="material-icons-outlined">touch_app</span>
                                <p>Select a device to view message history</p>
                            </div>
                        ) : messagesHistory.length === 0 && !refreshing ? (
                            <div className="empty-state">
                                <span className="material-icons-outlined">inbox</span>
                                <p>No messages found for this device</p>
                            </div>
                        ) : (
                            <table className="messages-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '60px' }}>Dir</th>
                                        <th>Payload</th>
                                        <th>Status</th>
                                        <th>Time</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {messagesHistory.map((msg, index) => (
                                        <tr key={msg.id || index}>
                                            <td>
                                                <span className={`direction-badge direction-${msg.direction?.toLowerCase()}`}>
                                                    {msg.direction === 'SENT' ? 'OUT' : 'IN'}
                                                </span>
                                            </td>
                                            <td style={{ fontFamily: 'monospace' }}>{msg.payload}</td>
                                            <td>
                                                <div className="status-indicator">
                                                    <span className={`status-dot-sm msg-status-${msg.status?.toLowerCase() || 'unknown'}`}></span>
                                                    {msg.status}
                                                </div>
                                            </td>
                                            <td>
                                                {new Date(msg.created_at || msg.createdAt).toLocaleString()}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MessagesPage;
